import moment from 'moment';
import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { toast } from '../../toasts';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack, MdKeyboardReturn } from 'react-icons/md';
import { Store } from '../../Store';
import CustomTypeahead from '../../components/CustomTypeahead';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { Button, Checkbox, FormField, Section, Select, controlClass, textareaClass } from '../../components/EntityList';
import accountService from '../../services/account.service';
import assetService from '../../services/asset.service';
import contractService from '../../services/contract.service';
import ConfigService from '../../services/settings.service';
import UserService from '../../services/user.service';
import { currencies, dueDays, getError, monthOptions, periodicities } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import '../../components/JsonDrivenForm/JsonDrivenForm.css';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	date: 'Fecha',
	dueDay: 'Primer vencimiento',
	selectedMonthOption: 'Generar desde',
	numMonths: 'Cantidad de meses',
	periodicity: 'Periodicidad',
	owner: 'Inquilino',
	property: 'Propiedad',
	periodInit: 'Inicio del período',
	amount: 'Monto',
	commonExpensesAmount: 'Gastos comunes',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_ACCOUNTS_SUCCESS':
		return { ...state, accounts: action.payload };
	case 'FETCH_ASSETS_SUCCESS':
		return { ...state, assets: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

/**
 * Full-page contract create/edit form (see screens/Debts/DebtEditor.jsx for
 * the pattern this mirrors). Renders only the form body — the page shell
 * (EntityList/PageHead) lives in the page components that use this
 * (NewContract.jsx, EditContract.jsx, and the contextual create/edit routes
 * reached from the account/asset tabs).
 */
const ContractEditor = ({
	mode = 'create',
	contractId = null,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	onReturn,
	returnLabel,
	preloadAccountId = null,
	preloadAssetId = null,
}) => {
	const { t } = useLabels();
	const { state } = useContext(Store);
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Ids injected via ?accountId=/?propertyId= after a "crear nueva cuenta de
	// inquilino" / "crear nueva propiedad" round trip (see NewAccount.jsx /
	// NewAsset.jsx). Kept in sync with the actual selection (see
	// syncInjectedParam below) rather than cleared outright — linkToCreate
	// carries location.search forward, so a second round trip (e.g. creating a
	// property right after a tenant) keeps both current selections.
	const injectedAccountId = searchParams.get('accountId');
	const injectedPropertyId = searchParams.get('propertyId');
	const isEditing = mode === 'edit';
	const currentDate = moment.utc();

	// Ids present at mount. syncInjectedParam rewrites the live params as the
	// user edits, so the edit-mode hydrate guards below must key off this
	// stable snapshot instead of the (now-mutable) params above.
	const injectedAtMount = useRef({
		accountId: injectedAccountId,
		propertyId: injectedPropertyId,
	});

	// Keep the URL mirroring the current selection for any field a "crear
	// nueva..." round trip can fill. The param is the only state that survives
	// the remount such a trip causes, so rewrite it on a manual pick (so that
	// choice survives the next trip) and drop it when the field is cleared.
	// Without this, a stale id from an earlier trip rides along in returnTo
	// and gets re-injected on top of the manual choice.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		// Pre-mark as consumed so the injection effect doesn't re-fetch a
		// record we already hold in state.
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const [{ loading, saving, error, accounts, assets }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		error: '',
		accounts: [],
		assets: [],
	});

	// Edit mode fetches the contract by id when the caller didn't already
	// supply initialData (mirrors DebtEditor.jsx's debtId-fetch effect).
	const [fetchedContract, setFetchedContract] = useState(null);
	const resolvedInitialData = initialData || fetchedContract;

	const [periodInit, setPeriodInit] = useState();
	const [periodEnd, setPeriodEnd] = useState();
	const [amount, setAmount] = useState();
	const [commonExpensesAmount, setCommonExpensesAmount] = useState('');
	const [owner, setOwner] = useState();
	const [field, setField] = useState();
	const [rental, setRental] = useState(true);
	const [property, setProperty] = useState();
	const [ubication] = useState();
	const [folder, setFolder] = useState();
	const [housingUnit, setHousingUnit] = useState();
	const [observations, setObservations] = useState();
	const [paymentMethod, setPaymentMethod] = useState();
	const [currency, setCurrency] = useState();
	const [hasPreviousDebts, setHasPreviousDebts] = useState(false);
	const [previousDebt, setPreviousDebt] = useState();
	const [previousDebtDate, setPreviousDebtDate] = useState();
	const [disabledOwner, setDisabledOwner] = useState(false);
	const [disabledProperty, setDisabledProperty] = useState(false);
	const [periodicity, setPeriodicity] = useState('mensual');
	const [periodicityNumMonths, setPeriodicityNumMonths] = useState();
	const [createWithPreviousDues, setCreateWithPreviousDues] = useState(false);
	const [createWithPreviousPayments, setCreateWithPreviousPayments] = useState(false);
	const [dueDay, setDueDay] = useState('10');
	const [firstDueExpiration, setFirstDueExpiration] = useState();
	const [numMonths, setNumMonths] = useState();
	const [selectedMonthOption, setSelectedMonthOption] = useState('Mes siguiente');
	const [date, setDate] = useState();
	const [paymentTo, setPaymentTo] = useState('Cliente');
	const [bankAccount, setBankAccount] = useState(null);
	const [companySettings, setCompanySettings] = useState(null);
	const [isInvalid, setIsInvalid] = useState(false);
	const [isPropertyInvalid, setIsPropertyInvalid] = useState(false);
	const [responsible, setResponsible] = useState(null);
	const [users, setUsers] = useState([]);
	const [hydrated, setHydrated] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	// Injected id already consumed. A ref, not state, so the guard is set
	// synchronously — StrictMode double-invokes effects in development and the
	// second run must not reprocess the same id.
	const consumedAccountId = useRef(null);
	const consumedPropertyId = useRef(null);

	// accountService.list() / assetService.list() run before a just-created
	// record exists, so keep the selected owner/property in the options.
	// Derived rather than pushed into the reducer so the in-flight list fetch
	// can't overwrite it.
	const accountOptions = useMemo(
		() => (owner?._id && !accounts.some((acc) => acc._id === owner._id) ? [owner, ...accounts] : accounts),
		[accounts, owner],
	);
	const assetOptions = useMemo(
		() => (property?._id && !assets.some((a) => a._id === property._id) ? [property, ...assets] : assets),
		[assets, property],
	);

	const clearFieldError = (fieldName) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[fieldName]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[fieldName];
			return nextErrors;
		});
	};

	const fetchContractById = async () => {
		if (initialData || !contractId) {
			return;
		}
		try {
			const result = await contractService.getDetails(contractId);
			setFetchedContract(result.data);
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			console.error(error);
			toast.error(t('msg.contractLoadFailed') || 'No se pudo obtener el contrato');
		}
	};

	const fetchAccounts = async () => {
		try {
			if (preloadAccountId) {
				const result = await accountService.getDetails(preloadAccountId);
				setOwner(result.data);
				setDisabledOwner(true);
				setCurrency(result.data.currency);
				if (paymentTo === 'Cliente' && result.data.currency) {
					updateBankAccount(result.data, paymentTo, result.data.currency);
				}
			} else {
				const result = await accountService.list();
				dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: result.data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.accountLoadFailed'));
		}
	};

	const fetchAssets = async () => {
		try {
			if (preloadAssetId) {
				const result = await assetService.getDetails(preloadAssetId);
				setProperty(result.data);
				setFolder(result.data.folder);
				setHousingUnit(result.data.housingUnit || '');
				setDisabledProperty(true);
			} else {
				const { data } = await assetService.list();
				dispatch({ type: 'FETCH_ASSETS_SUCCESS', payload: data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.assetLoadFailed'));
		}
	};

	const fetchUsers = async () => {
		try {
			const result = await UserService.list();
			setUsers(result.data);
		} catch (error) {
			console.error('Error fetching users:', error);
		}
	};

	const fetchSettings = async () => {
		try {
			const result = await ConfigService.listPublic();
			setCompanySettings(result.data);
		} catch (error) {
			console.error('Error fetching settings:', error);
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				await Promise.all([fetchContractById(), fetchAccounts(), fetchAssets(), fetchUsers(), fetchSettings()]);
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				console.error(ex);
				dispatch({ type: 'FETCH_FAIL', payload: getError(ex) });
			}
		};
		fetchData();
	}, [contractId, preloadAccountId, preloadAssetId]);

	useEffect(() => {
		generateDate();
		dateHandler();
	}, [date, selectedMonthOption, dueDay, numMonths]);

	// Hydrate the form from the contract being edited, once the supporting
	// lists are available (the owner/property typeaheads need their options
	// loaded before a selection can be shown).
	useEffect(() => {
		if (!isEditing || !resolvedInitialData || hydrated) return;
		if (!preloadAccountId && accounts.length === 0) return;
		if (!preloadAssetId && assets.length === 0) return;

		const data = resolvedInitialData;
		if (data.amount) setAmount(data.amount);
		if (data.periodInit) setPeriodInit(moment(data.periodInit).toDate());
		if (data.periodEnd) setPeriodEnd(moment(data.periodEnd).toDate());
		if (data.field) setField(data.field);
		if (data.housingUnit) setHousingUnit(data.housingUnit);
		if (data.folder) setFolder(data.folder);
		if (data.observations) setObservations(data.observations);
		if (data.paymentMethod) setPaymentMethod(data.paymentMethod);
		if (data.currency) setCurrency(data.currency);
		if (data.hasPreviousDebts !== undefined) setHasPreviousDebts(data.hasPreviousDebts);
		if (data.previousDebt) setPreviousDebt(data.previousDebt);
		if (data.previousDebtDate) setPreviousDebtDate(moment(data.previousDebtDate).toDate());
		if (data.periodicity) setPeriodicity(data.periodicity);
		if (data.periodicityNumMonths) setPeriodicityNumMonths(data.periodicityNumMonths);
		if (data.dueDay) setDueDay(data.dueDay);
		if (data.firstDueExpiration) setFirstDueExpiration(data.firstDueExpiration);
		if (data.numMonths) setNumMonths(data.numMonths);
		if (data.selectedMonthOption) setSelectedMonthOption(data.selectedMonthOption);
		if (data.date) setDate(moment(data.date).toDate());
		else if (data.periodInit) setDate(moment(data.periodInit).toDate());
		if (data.paymentTo) setPaymentTo(data.paymentTo);
		if (data.rental !== undefined) setRental(data.rental);

		// Skip when an account/asset was just injected via ?accountId=/?propertyId=
		// (see the effects below) — otherwise whichever of the two writers to
		// owner/property resolves last wins, nondeterministically.
		// injectedAtMount is a stable snapshot (unlike the live injected*Id
		// below, which syncInjectedParam rewrites as the user edits), so this
		// guard doesn't flip mid-life.
		if (data.owner && !preloadAccountId && !injectedAtMount.current.accountId) {
			if (typeof data.owner === 'object' && data.owner._id) {
				setOwner(data.owner);
				if (data.owner.currency) setCurrency(data.owner.currency);
			} else {
				const foundOwner = accounts.find((acc) => acc._id === data.owner || acc._id === data.owner._id);
				if (foundOwner) {
					setOwner(foundOwner);
					if (foundOwner.currency) setCurrency(foundOwner.currency);
				}
			}
		}

		if (data.property && !preloadAssetId && !injectedAtMount.current.propertyId) {
			if (typeof data.property === 'object' && data.property._id) {
				setProperty(data.property);
				if (data.property.folder) setFolder(data.property.folder);
				if (data.property.housingUnit) setHousingUnit(data.property.housingUnit);
			} else {
				const foundProperty = assets.find((asset) => asset._id === data.property || asset._id === data.property._id);
				if (foundProperty) {
					setProperty(foundProperty);
					if (foundProperty.folder) setFolder(foundProperty.folder);
					if (foundProperty.housingUnit) setHousingUnit(foundProperty.housingUnit);
				}
			}
		}

		if (data.bankAccount && typeof data.bankAccount === 'object') {
			setBankAccount(data.bankAccount);
		}

		setHydrated(true);
	}, [isEditing, resolvedInitialData, hydrated, accounts, assets, preloadAccountId, preloadAssetId]);

	// Handle responsible field when users are loaded
	useEffect(() => {
		if (resolvedInitialData && resolvedInitialData.responsible && users.length > 0) {
			if (typeof resolvedInitialData.responsible === 'object' && resolvedInitialData.responsible._id) {
				setResponsible(resolvedInitialData.responsible);
			} else {
				const foundUser = users.find(
					(user) => user._id === resolvedInitialData.responsible || user._id === resolvedInitialData.responsible._id,
				);
				if (foundUser) setResponsible(foundUser);
			}
		}
	}, [resolvedInitialData, users]);

	// Update bank account when paymentTo, currency, owner, and companySettings are available
	useEffect(() => {
		if (resolvedInitialData && owner && paymentTo && currency) {
			if (resolvedInitialData.bankAccount && typeof resolvedInitialData.bankAccount === 'object') {
				setBankAccount(resolvedInitialData.bankAccount);
			} else if (companySettings || paymentTo === 'Cliente') {
				updateBankAccount(owner, paymentTo, currency);
			}
		}
	}, [resolvedInitialData, owner, paymentTo, currency, companySettings]);

	// Auto-select an account created via the "crear nueva cuenta de inquilino"
	// link (CustomTypeahead's linkToCreate -> NewAccount.jsx), which returns
	// here with ?accountId=<id>. The ref only de-dupes the fetch — StrictMode
	// double-invokes this in development. Do NOT reintroduce an isCurrent
	// cleanup flag here: combined with clearing the dependency (e.g. via a
	// state-clearing navigate) it aborts the invocation that does the work.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		consumedAccountId.current = injectedAccountId;

		(async () => {
			try {
				const { data } = await accountService.getDetails(injectedAccountId);
				setOwner(data);
				setIsInvalid(false);
				if (data.currency) setCurrency(data.currency);
				clearFieldError('owner');
				if (paymentTo && data.currency) {
					updateBankAccount(data, paymentTo, data.currency);
				}
			} catch (error) {
				console.error(error);
				toast.error(t('msg.accountLoadFailed'));
			}
		})();
	}, [injectedAccountId]);

	// Auto-select a property created via the "crear nueva propiedad" link
	// (CustomTypeahead's linkToCreate -> NewAsset.jsx), same round trip as the
	// account handling above.
	useEffect(() => {
		if (!injectedPropertyId || consumedPropertyId.current === injectedPropertyId) return;
		consumedPropertyId.current = injectedPropertyId;

		(async () => {
			try {
				const { data } = await assetService.getDetails(injectedPropertyId);
				setProperty(data);
				setIsPropertyInvalid(false);
				setFolder(data.folder);
				setHousingUnit(data.housingUnit || '');
				clearFieldError('property');
			} catch (error) {
				console.error(error);
				toast.error(t('msg.assetLoadFailed'));
			}
		})();
	}, [injectedPropertyId]);

	const dateHandler = () => {
		if (moment.utc(firstDueExpiration).isBefore(moment.utc(currentDate))) {
			setCreateWithPreviousDues(true);
		} else {
			setCreateWithPreviousDues(false);
			setCreateWithPreviousPayments(false);
		}
	};

	const generateDate = () => {
		if (!date) return;
		const currentDate = moment.utc(date, 'YYYY-MM-DD');
		let futureDate;

		if (selectedMonthOption === 'Mes actual') {
			futureDate = currentDate.clone();
		} else if (selectedMonthOption === 'Mes siguiente') {
			futureDate = currentDate.clone().add(1, 'month');
		} else if (selectedMonthOption === 'En tres meses' || selectedMonthOption === 'En seis meses') {
			const monthsToAdd = selectedMonthOption === 'En tres meses' ? 3 : 6;
			futureDate = currentDate.clone().add(monthsToAdd, 'month');
		} else if (selectedMonthOption === 'Otro') {
			futureDate = currentDate.clone().add(numMonths, 'month');
		}

		if (futureDate) {
			futureDate.date(Math.min(dueDay, futureDate.daysInMonth()));
			setFirstDueExpiration(moment.utc(futureDate).format('YYYY-MM-DD'));
		}
	};

	const getFieldErrors = () => {
		const emptyFields = [];
		if (!date) emptyFields.push('date');
		if (!dueDay) emptyFields.push('dueDay');
		if (!selectedMonthOption) emptyFields.push('selectedMonthOption');
		if (selectedMonthOption === 'Otro' && !numMonths) emptyFields.push('numMonths');
		if (!periodicity) emptyFields.push('periodicity');
		if (!owner) emptyFields.push('owner');
		if (!property) emptyFields.push('property');
		if (!periodInit) emptyFields.push('periodInit');
		if (!amount) emptyFields.push('amount');
		const errors = {};
		emptyFields.forEach((key) => {
			errors[key] = REQUIRED_FIELD_MESSAGE;
		});
		if (!isEditing && commonExpensesAmount !== '') {
			const parsedCommonExpenses = Number(commonExpensesAmount);
			if (!Number.isFinite(parsedCommonExpenses) || parsedCommonExpenses < 0) {
				errors.commonExpensesAmount = 'Ingresá un monto válido mayor o igual a cero.';
				emptyFields.push('commonExpensesAmount');
			}
		}
		return { errors, emptyFields };
	};

	const handleAccountChange = async (selected) => {
		const selectedAccount = selected[0];
		// Validate against accountOptions, not accounts — a just-created account is
		// only present in the derived list, and rejecting it would wipe the selection.
		const isValid = selectedAccount ? accountOptions.includes(selectedAccount) : false;
		setIsInvalid(!isValid);
		if (isValid) {
			syncInjectedParam('accountId', selectedAccount._id, consumedAccountId);
			setOwner(selected[0]);
			setCurrency(selectedAccount.currency);
			updateBankAccount(selectedAccount, paymentTo, selectedAccount.currency);
			clearFieldError('owner');
		} else {
			syncInjectedParam('accountId', null, consumedAccountId);
			setOwner(null);
			setBankAccount(null);
		}
	};

	const handlePaymentToChange = (e) => {
		const newPaymentTo = e.target.value;
		setPaymentTo(newPaymentTo);
		if (owner && currency) {
			updateBankAccount(owner, newPaymentTo, currency);
		}
	};

	const handleCurrencyChange = (newCurrency) => {
		setCurrency(newCurrency);
		if (owner && paymentTo) {
			updateBankAccount(owner, paymentTo, newCurrency);
		}
	};

	const updateBankAccount = async (account, paymentToValue, currencyValue) => {
		if (paymentToValue === 'Cliente') {
			if (currencyValue === 'USD' && account.bankAccountUSD) {
				setBankAccount(account.bankAccountUSD);
			} else if (currencyValue === 'UYU' && account.bankAccountUYU) {
				setBankAccount(account.bankAccountUYU);
			} else {
				setBankAccount(null);
			}
		} else if (paymentToValue === 'Inmobiliaria' && companySettings) {
			if (currencyValue === 'USD' && companySettings.companyBankAccountUSD) {
				setBankAccount(companySettings.companyBankAccountUSD);
			} else if (currencyValue === 'UYU' && companySettings.companyBankAccountUYU) {
				setBankAccount(companySettings.companyBankAccountUYU);
			} else {
				setBankAccount(null);
			}
		} else {
			setBankAccount(null);
		}
	};

	const handlePropertyChange = (selected) => {
		if (!selected || selected.length === 0) {
			const selectedProperty = selected[0];
			// Validate against assetOptions, not assets — a just-created property is
			// only present in the derived list, and rejecting it would wipe the selection.
			const isPropertyInvalid = selectedProperty ? assetOptions.includes(selectedProperty) : false;
			syncInjectedParam('propertyId', null, consumedPropertyId);
			setIsPropertyInvalid(!isPropertyInvalid);
			setProperty(null);
			setFolder('');
			setHousingUnit('');
		} else {
			const selectedProperty = selected[0];
			const isPropertyInvalid = selectedProperty ? assetOptions.includes(selectedProperty) : false;
			syncInjectedParam('propertyId', selectedProperty?._id, consumedPropertyId);
			setIsPropertyInvalid(!isPropertyInvalid);
			setProperty(selectedProperty);
			setFolder(selectedProperty.folder);
			setHousingUnit(selectedProperty.housingUnit || '');
			clearFieldError('property');
		}
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const { errors, emptyFields } = getFieldErrors();
		if (emptyFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = emptyFields.map((key) => FIELD_LABELS[key] || key);
			dispatch({
				type: 'SAVE_FAIL',
				payload: `${t('msg.completeFields') || 'Completá los siguientes campos'}: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		setFieldErrors({});

		try {
			dispatch({ type: 'SAVE_REQUEST' });
			const requestData = {
				date,
				amount,
				periodInit,
				periodEnd,
				owner: owner[0] || owner,
				field,
				rental,
				property: property[0] || property,
				ubication,
				folder,
				housingUnit,
				observations,
				paymentMethod,
				currency,
				periodicity,
				dueDay,
				selectedMonthOption,
				firstDueExpiration: moment.utc(firstDueExpiration),
				createWithPreviousDues,
				createWithPreviousPayments,
				createdBy: state.userInfo._id,
				status: '',
				paymentTo,
				bankAccount: bankAccount?._id || bankAccount,
				responsible: responsible?._id || responsible,
			};
			if (periodicity === 'otra') {
				requestData.periodicityNumMonths = periodicityNumMonths;
			}
			if (hasPreviousDebts) {
				requestData.hasPreviousDebts = hasPreviousDebts;
				requestData.previousDebt = previousDebt;
				requestData.previousDebtDate = previousDebtDate;
			} else {
				requestData.hasPreviousDebts = false;
				requestData.previousDebt = '';
				requestData.previousDebtDate = '';
			}
			if (selectedMonthOption === 'Otro') {
				requestData.numMonths = numMonths;
			}
			if (!isEditing && commonExpensesAmount !== '') {
				requestData.commonExpensesAmount = Number(commonExpensesAmount);
			}

			let savedContractId = contractId || resolvedInitialData?._id;

			if (isEditing) {
				const result = await contractService.edit(savedContractId, requestData);
				savedContractId = result.data?.contract?._id || savedContractId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.contractUpdated'));
			} else {
				const result = await contractService.create(requestData);
				savedContractId = result.data?.contract?._id;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.contractCreated'));
			}

			if (onSaved) {
				onSaved(savedContractId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			toast.error(message);
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? t('action.update') : t('action.add'));

	return (
		<Form id='contract-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos del contrato.' : 'Completá los datos para crear el contrato.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					{onReturn && (
						<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={saving}>
							{returnLabel || t('action.back')}
						</Button>
					)}
					<Button
						variant='primary'
						icon={isEditing ? <FaRegSave /> : <MdAdd />}
						type='submit'
						disabled={saving || loading}
					>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title={t('title.dueGenerationData')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-4'>
					<FormField id='contract-date' label={t('form.debtStartDate')} required>
						<DatePicker
							id='contract-date'
							selected={date}
							onChange={(d) => {
								setDate(d);
								clearFieldError('date');
							}}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
						/>
						<FieldError message={fieldErrors.date} />
					</FormField>
					<FormField id='contract-due-day' label={t('form.firstDueDay')} required>
						<Select
							id='contract-due-day'
							options={dueDays}
							value={dueDay}
							onChange={(e) => {
								setDueDay(e.target.value);
								clearFieldError('dueDay');
							}}
						/>
						<FieldError message={fieldErrors.dueDay} />
					</FormField>
					<FormField id='contract-generate-from' label={t('form.generateFrom')} required>
						<Select
							id='contract-generate-from'
							options={monthOptions}
							value={selectedMonthOption}
							onChange={(e) => {
								setSelectedMonthOption(e.target.value);
								clearFieldError('selectedMonthOption');
							}}
						/>
						<FieldError message={fieldErrors.selectedMonthOption} />
					</FormField>
					{selectedMonthOption === 'Otro' && (
						<FormField id='contract-num-months' label={t('form.numMonths')} required>
							<Form.Control
								id='contract-num-months'
								className={controlClass}
								type='number'
								min='24'
								value={numMonths}
								onChange={(e) => {
									setNumMonths(parseInt(e.target.value, 10));
									clearFieldError('numMonths');
								}}
								onInput={(e) => {
									const inputValue = parseInt(e.target.value, 10);
									const maxValue = 24;
									if (maxValue !== null && inputValue > maxValue) {
										e.target.value = maxValue;
									}
								}}
							/>
							<FieldError message={fieldErrors.numMonths} />
						</FormField>
					)}
					<FormField id='contract-periodicity' label={t('field.periodicity')} required>
						<Select
							id='contract-periodicity'
							options={periodicities}
							value={periodicity}
							onChange={(e) => {
								setPeriodicity(e.target.value);
								clearFieldError('periodicity');
							}}
						/>
						<FieldError message={fieldErrors.periodicity} />
					</FormField>
					{periodicity === 'otra' && (
						<FormField id='contract-periodicity-months' label={t('form.periodicityMonths')}>
							<FormatedNumberInput
								className={controlClass}
								onChange={(e) => setPeriodicityNumMonths(e.target.value)}
								min={1}
								noDecimal={true}
							/>
						</FormField>
					)}
				</div>
				{date && (
					<p className='tw:mt-3 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>
						{`${t('msg.firstExpiration')}: ${moment.utc(firstDueExpiration).format('DD-MM-YYYY')}`}
					</p>
				)}
			</Section>

			{createWithPreviousDues && (
				<Section title={t('title.debtWithPreviousDues')} description={t('msg.previousDuesWarning')}>
					<Checkbox
						label={t('form.markPreviousDuesPaid')}
						checked={createWithPreviousPayments}
						onChange={() => setCreateWithPreviousPayments(!createWithPreviousPayments)}
					/>
				</Section>
			)}

			<Section title={t('title.contractData')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					{!preloadAssetId && (
						<FormField id='contract-property' label={t('field.property')} required>
							<CustomTypeahead
								id='assets'
								labelKey={(option) => `${option.name}`}
								onChange={handlePropertyChange}
								options={assetOptions}
								disabled={disabledProperty}
								selected={property ? [property] : []}
								placeholder={t('form.placeholder.searchProperty')}
								linkToCreate={`/AdminScreen/newAsset?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva propiedad'
								variant='entity'
							/>
							{isPropertyInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidProperty')}</div>}
							<FieldError message={fieldErrors.property} />
						</FormField>
					)}
					{preloadAssetId && (
						<FormField id='contract-property' label={t('field.property')}>
							<Form.Control id='contract-property' className={controlClass} readOnly type='text' value={property ? property.name : ''} />
						</FormField>
					)}
					{!preloadAccountId && (
						<FormField id='contract-owner' label={t('entity.tenant')} required>
							<CustomTypeahead
								id='accounts'
								labelKey={(option) => `${option.name}`}
								onChange={handleAccountChange}
								options={accountOptions}
								disabled={disabledOwner}
								selected={owner ? [owner] : []}
								placeholder={t('form.placeholder.searchTenant')}
								isInvalid={isInvalid}
								linkToCreate={`/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva cuenta de inquilino'
								variant='entity'
							/>
							{isInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidAccount')}</div>}
							<FieldError message={fieldErrors.owner} />
						</FormField>
					)}
					{preloadAccountId && (
						<FormField id='contract-owner' label={t('entity.tenant')}>
							<Form.Control id='contract-owner' className={controlClass} readOnly type='text' value={owner ? owner.name : ''} />
						</FormField>
					)}
					<FormField id='contract-period-init' label={t('field.periodInit')} required>
						<DatePicker
							id='contract-period-init'
							selected={periodInit ? moment(periodInit).toDate() : null}
							onChange={(d) => {
								setPeriodInit(d);
								clearFieldError('periodInit');
							}}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
						/>
						<FieldError message={fieldErrors.periodInit} />
					</FormField>
					<FormField id='contract-period-end' label={t('field.periodEnd')}>
						<DatePicker
							id='contract-period-end'
							selected={periodEnd ? moment(periodEnd).toDate() : null}
							onChange={(d) => setPeriodEnd(d)}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
						/>
					</FormField>
					<FormField id='contract-amount' label={t('field.amount')} required>
						<FormatedNumberInput
							className={controlClass}
							type='number'
							value={amount}
							onChange={(e) => {
								setAmount(e.target.value);
								clearFieldError('amount');
							}}
							min={0}
						/>
						<FieldError message={fieldErrors.amount} />
					</FormField>
					{!isEditing && (
						<FormField id='contract-common-expenses' label='Gastos comunes'>
							<FormatedNumberInput
								className={controlClass}
								type='number'
								value={commonExpensesAmount}
								onChange={(e) => {
									setCommonExpensesAmount(e.target.value);
									clearFieldError('commonExpensesAmount');
								}}
								min={0}
							/>
							<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-3'>
								Opcional. Crea un gasto mensual cobrado al inquilino.
							</p>
							<FieldError message={fieldErrors.commonExpensesAmount} />
						</FormField>
					)}
					<FormField id='contract-currency' label={t('field.currency')}>
						<Select
							id='contract-currency'
							options={currencies}
							value={currency}
							onChange={(e) => handleCurrencyChange(e.target.value)}
						/>
					</FormField>
					<div className='tw:flex tw:items-center tw:h-[34px] md:tw:col-span-2'>
						<Checkbox
							label={t('form.isRental')}
							checked={rental}
							onChange={(e) => setRental(e.target.checked)}
						/>
					</div>
					{rental && (
						<>
							<FormField id='contract-payment-to' label={t('form.paymentTo')}>
								<Select
									id='contract-payment-to'
									options={[
										{ value: 'Cliente', label: t('enum.paymentTo.cliente') },
										{ value: 'Inmobiliaria', label: t('enum.paymentTo.inmobiliaria') },
									]}
									value={paymentTo}
									onChange={handlePaymentToChange}
								/>
							</FormField>
							{bankAccount && (
								<FormField id='contract-bank-account' label={t('form.paymentAccount')}>
									<Form.Control
										id='contract-bank-account'
										className={controlClass}
										type='text'
										value={`${bankAccount.bank} - ${bankAccount.accountNumber} (${bankAccount.currency})`}
										disabled
									/>
								</FormField>
							)}
						</>
					)}
					<FormField id='contract-field' label={t('title.details')} className='md:tw:col-span-2'>
						<Form.Control
							id='contract-field'
							className={textareaClass}
							type='text'
							as='textarea'
							rows={10}
							defaultValue={field ? field : null}
							onChange={(e) => setField(e.target.value)}
						/>
					</FormField>
					<FormField id='contract-folder' label={t('field.folder')}>
						<Form.Control
							id='contract-folder'
							className={controlClass}
							type='text'
							defaultValue={folder ? folder : null}
							onChange={(e) => setFolder(e.target.value)}
						/>
					</FormField>
					<FormField id='contract-housing-unit' label={t('field.housingUnit')}>
						<Form.Control
							id='contract-housing-unit'
							className={controlClass}
							type='text'
							value={housingUnit || ''}
							onChange={(e) => setHousingUnit(e.target.value)}
						/>
					</FormField>
					<FormField id='contract-payment-method' label={t('field.collectionMethod')}>
						<Form.Control
							id='contract-payment-method'
							className={controlClass}
							type='text'
							defaultValue={paymentMethod ? paymentMethod : null}
							onChange={(e) => setPaymentMethod(e.target.value)}
						/>
					</FormField>
					<FormField id='contract-observations' label={t('field.observations')} className='md:tw:col-span-2'>
						<Form.Control
							id='contract-observations'
							className={textareaClass}
							type='text'
							as='textarea'
							rows={10}
							defaultValue={observations ? observations : null}
							onChange={(e) => setObservations(e.target.value)}
						/>
					</FormField>
					<FormField id='contract-responsible' label={t('field.responsible')}>
						<CustomTypeahead
							id='users'
							labelKey={(option) => `${option.name}`}
							onChange={(selected) => setResponsible(selected && selected.length > 0 ? selected[0] : null)}
							options={users}
							placeholder={t('form.placeholder.searchResponsibleUser')}
							selected={responsible ? [responsible] : null}
							variant='entity'
						/>
					</FormField>
				</div>
			</Section>
		</Form>
	);
};

ContractEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	contractId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func,
	onSaved: PropTypes.func,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	onReturn: PropTypes.func,
	returnLabel: PropTypes.string,
	preloadAccountId: PropTypes.string,
	preloadAssetId: PropTypes.string,
};

export default ContractEditor;
